import { useEffect } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { httpRequest } from "@/lib/axios";
import echo from "@/lib/echo";
import { useNotificationIdentity } from "./use-notification-identity";
import { Pagination } from "@/types";

export type Notification = {
  id: string;
  title: string;
  message: string;
  timestamp: string;
  action_path: string | null;
  action_label: string | null;
  entity_type: string | null;
  entity_id: string | null;
  read_at: string | null;
  created_at: string;
  updated_at: string;
};

export interface RequestPositionResponse {
  message: string;
  data: Notification;
}

export type NotificationsResponse = {
  items: Notification[];
  data: Pagination;
};

type Props = {
  realtime?: boolean;
  page?: string | null;
  per_page?: string | null;
  unread?: string | null;
  read?: string | null;
}

async function fetchNotifications({page='1', per_page='10', read, unread}:Props, signal?: AbortSignal): Promise<NotificationsResponse> {
  const response = await httpRequest.get<NotificationsResponse>(
    `/notifications?page=${page ?? "1"}&per_page=${per_page ?? "10"}&filter=${unread ? "unread" : read ? "read" : ""}`,
    { signal }
  );
  return response.data;
}

export function useFetchNotificationsQuery({page, per_page, read, unread, realtime = false}: Props) {
  const { tenantId, userId } = useNotificationIdentity();
  const queryClient = useQueryClient();

  // Only the navbar owns this listener; list consumers share the query cache.
  useEffect(() => {
    if (!realtime || !tenantId || !userId || !echo) return;
    const channel = echo.private(`App.Models.User.${userId}`);
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications", tenantId, userId] });
    };
    channel.listen(".NotificationCreated", refresh);
    return () => {
      channel.stopListening(".NotificationCreated", refresh);
    };
  }, [realtime, tenantId, userId, queryClient]);
  const notificationsQuery = useQuery({
    queryKey: ["notifications", tenantId, userId, page ?? "1", per_page ?? "10", read, unread],
    queryFn: ({ signal }) => fetchNotifications({page, per_page, read, unread}, signal),
    enabled: Boolean(tenantId && userId),
    keepPreviousData: false,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
  });

  return {
    ...notificationsQuery,
    data: notificationsQuery.data,
  };
}
